import { fetchSchoolStudents } from '@/api/resources/student.api';
import { useDisplayName } from '@/composables/useDisplayName';
import { usePermissions } from '@/composables/usePermissions';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { studentKeys } from './student.keys';

export function useSchoolStudents(isActive: MaybeRefOrGetter<boolean> = true) {
  const { nameSortation } = useDisplayName();
  const { hasTenantAdminAccess } = usePermissions();
  const whoAmIStore = useWhoAmIStore();
  const tenantId = computed(() => whoAmIStore.whoAmI?.currentTenantId);
  const canRead = computed(() => tenantId.value != null && hasTenantAdminAccess.value);
  const enabled = computed(() => toValue(isActive) && canRead.value);
  const query = useQueryWithToast({
    queryKey: computed(() => studentKeys.schoolList(tenantId.value, nameSortation.value, hasTenantAdminAccess.value)),
    enabled,
    queryFn: () => (canRead.value ? fetchSchoolStudents() : []),
  });

  // Dialog setup sometimes needs the list before copying it into its form state.
  const refresh = async (): Promise<void> => {
    await query.refetch({ cancelRefetch: false, throwOnError: true });
  };

  return {
    refresh,
    allStudentsAdmin: computed(() => (canRead.value ? (query.data.value ?? []) : [])),
    isLoading: computed(() => query.isLoading.value || query.isFetching.value),
  };
}
